import React, { useState, useEffect, useCallback } from "react";
import copiaAIIcon from "../../images/CopiaAi-Logo.png"

function ToggleButton({ toggleChat, unreadMessages, chatOpen, minimized }) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [currentPrompt, setCurrentPrompt] = useState("");
    const [isHovering, setIsHovering] = useState(false);
    const [promptDismissed, setPromptDismissed] = useState(false);

    const engagementPrompts = [
        "Hey, need quick clarity? Ask me: 'Who is the cleaning provider at Slug & Lettuce Birmingham?'",
        "Unsure where you're overspending? Try: 'Which of my sites spend the most on security?'",
        "Want to check value for money? Ask: 'What's the average hourly rate paid to cleaners in my area?'",
        "Curious about patterns? Try: 'Show me the weekly spend trend for security at my venues.'",
        "Need to challenge a cost? Ask: 'Which sites are being billed for scheduled hours instead of attended hours?'",
        "Wondering who's reliable? Ask: 'Which security providers have the most missed or late shifts?'",
        "Checking entertainment usage? Ask: 'How many DJ shifts did Slug & Lettuce Cardiff run last month?'",
        "Looking for hot spots? Ask: 'Which sites have the highest cleaning cost per hour?'",
        "Want to see provider performance? Try: 'Show me fulfilment rates for my security suppliers.'",
        "Comparing suppliers? Ask: 'Which cleaning company gives the best fulfilment in my region?'",
        "Need insight fast? Ask: 'What were my total security costs last week?'",
        "Trying to reduce spend? Ask: 'Which of my sites have rising cleaning costs?'",
        "Want to review a single venue? Ask: 'Give me the entertainment spend breakdown for Yates Manchester.'",
        "Checking compliance? Ask: 'Which shifts last night had missing or late book-ons?'",
        "Wonder what's new? Ask: 'Show me unusual spikes in labour costs this week.'",
        "Want simple comparisons? Ask: 'Which of my venues spend more than £X per week on DJs?'",
        "Tracking your patch? Ask: 'Show me all security shifts cancelled in the last 7 days.'",
        "Looking for quick wins? Ask: 'Which sites could save the most by switching to attended-hour billing?'",
        "Want a quick summary? Ask: 'Give me my weekly cleaning, security, and entertainment spend across all sites.'"
    ];

    const getRandomPrompt = useCallback(() => {
        const randomIndex = Math.floor(Math.random() * engagementPrompts.length);
        return engagementPrompts[randomIndex];
    }, []);

    useEffect(() => {
        if (chatOpen || promptDismissed) {
            setShowPrompt(false);
            return;
        }

        const interval = setInterval(() => {
            if (!isHovering && !chatOpen && !promptDismissed) {
                setCurrentPrompt(getRandomPrompt());
                setShowPrompt(true);
            }
        }, 45000);

        return () => clearInterval(interval);

    }, [chatOpen, isHovering, promptDismissed, getRandomPrompt]);

    useEffect(() => {
        if (!showPrompt) return;

        const hideTimer = setTimeout(() => {
            setShowPrompt(false);
        }, 10000);

        return () => clearTimeout(hideTimer);
    }, [showPrompt]);

    // Re-enable prompts after 5 minutes of being dismissed
    useEffect(() => {
        if (promptDismissed) {
            const resetTimer = setTimeout(() => {
                setPromptDismissed(false);
            }, 300000);

            return () => clearTimeout(resetTimer);
        }
    }, [promptDismissed]);

    const handleDismissPrompt = (e) => {
        e.stopPropagation();
        setShowPrompt(false);
        setPromptDismissed(true);
    };

    const handlePromptClick = () => {
        setShowPrompt(false);
        toggleChat();
    };

    const styles = {
        toggleButton: {
            position: 'fixed',
            top: '25px',
            right: '25px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease',
            overflow: 'visible',
            animation: chatOpen ? 'none' : 'enhancedPulse 2s infinite ease-in-out',
            cursor: 'pointer',
            filter: 'brightness(1.1)'
        },
        notificationBadge: {
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            backgroundColor: '#ff3b30',
            color: 'white',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            border: '2px solid white',
            animation: 'bounce 1s infinite'
        },
        robotImage: {
            width: "50px",
            filter: 'brightness(1.1) contrast(1.05)'
        },
        speechBubble: {
            position: 'fixed',
            top: '95px',
            right: '25px',
            maxWidth: '320px',
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '16px 20px',
            boxShadow: '0 8px 24px rgba(12, 2, 47, 0.2), 0 0 0 1px rgba(12, 2, 47, 0.1)',
            zIndex: 1049,
            animation: 'slideInBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            border: '2px solid #0c022f'
        },
        speechBubbleArrow: {
            position: 'absolute',
            top: '-12px',
            right: '24px',
            width: '0',
            height: '0',
            borderLeft: '12px solid transparent',
            borderRight: '12px solid transparent',
            borderBottom: '12px solid #0c022f'
        },
        speechBubbleArrowInner: {
            position: 'absolute',
            top: '-10px',
            right: '24px',
            width: '0',
            height: '0',
            borderLeft: '12px solid transparent',
            borderRight: '12px solid transparent',
            borderBottom: '12px solid white'
        },
        promptText: {
            fontSize: '14px',
            lineHeight: '1.5',
            color: '#0c022f',
            marginBottom: '12px',
            fontWeight: '500'
        },
        dismissButton: {
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'transparent',
            border: 'none',
            fontSize: '18px',
            color: '#6c757d',
            cursor: 'pointer',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s, color 0.2s',
            padding: '0'
        },
        ctaButton: {
            backgroundColor: '#0c022f',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            width: '100%',
            transition: 'background-color 0.2s, transform 0.1s',
            marginTop: '4px'
        }
    };

    const animations = `
        @keyframes enhancedPulse {
            0% {
                transform: scale(1);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15),
                            0 0 0 0 rgba(12, 2, 47, 0.7),
                            0 0 20px rgba(12, 2, 47, 0.3);
            }
            50% {
                transform: scale(1.05);
                box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2),
                            0 0 0 12px rgba(12, 2, 47, 0.2),
                            0 0 40px rgba(12, 2, 47, 0.4);
            }
            100% {
                transform: scale(1);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15),
                            0 0 0 0 rgba(12, 2, 47, 0),
                            0 0 20px rgba(12, 2, 47, 0.3);
            }
        }

        @keyframes slideInBounce {
            0% {
                opacity: 0;
                transform: translateY(-20px) scale(0.8);
            }
            60% {
                opacity: 1;
                transform: translateY(5px) scale(1.05);
            }
            100% {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }

        @keyframes bounce {
            0%, 100% {
                transform: scale(1);
            }
            50% {
                transform: scale(1.15);
            }
        }

        .chat-button:hover {
            transform: scale(1.1) !important;
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25),
                        0 0 0 8px rgba(12, 2, 47, 0.15),
                        0 0 50px rgba(12, 2, 47, 0.5) !important;
            filter: brightness(1.2) !important;
        }

        .speech-bubble:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(12, 2, 47, 0.25), 0 0 0 1px rgba(12, 2, 47, 0.1);
        }

        .cta-button:hover {
            background-color: #1a0a4a;
            transform: translateY(-1px);
        }

        .cta-button:active {
            transform: translateY(0);
        }

        .dismiss-button:hover {
            background-color: rgba(0, 0, 0, 0.05);
            color: #0c022f;
        }
    `;

    return (
        <>
            <style>{animations}</style>

            {showPrompt && !chatOpen && (
                <div
                    style={styles.speechBubble}
                    className="speech-bubble"
                    onClick={handlePromptClick}
                >
                    <div style={styles.speechBubbleArrow}></div>
                    <div style={styles.speechBubbleArrowInner}></div>
                    <button
                        style={styles.dismissButton}
                        className="dismiss-button"
                        onClick={handleDismissPrompt}
                        aria-label="Dismiss"
                    >
                        ×
                    </button>
                    <div style={styles.promptText}>
                        {currentPrompt}
                    </div>
                    <button
                        style={styles.ctaButton}
                        className="cta-button"
                    >
                        Try it now →
                    </button>
                </div>
            )}

            <button
                onClick={toggleChat}
                style={styles.toggleButton}
                className="btn btn-primary chat-button"
                type="button"
                aria-haspopup="dialog"
                aria-expanded={chatOpen}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <img
                    src={copiaAIIcon}
                    alt="Copia Robot"
                    style={styles.robotImage}
                />

                {unreadMessages > 0 && (
                    <div style={styles.notificationBadge}>
                        {unreadMessages}
                    </div>
                )}
            </button>
        </>
    );
}

export default ToggleButton;